<template>
  <div
    class="order-page"
    :class="{
      'is-to-ship': isToShip,
      'no-operations': noOperations
    }"
    :data-is-creator="isCreator"
  >
    <Empty v-if="loaded && !hasOrderPermission" text="您无权查看该订单" />
    <template v-else-if="loaded">
      <!----| SECTIONS |---->
      <!-- 顶部提示，它将移动到Layout顶部 -->
      <OrderShareTips
        :order="order"
        :is-creator="isCreator"
        :refund-status="computedRefundStatus"
        :is-base-order="isBaseOrder"
      />
      <!-- 顶部转态栏 -->
      <OrderStatus
        :order="order"
        :is-creator="isCreator"
        :is-base-order="isBaseOrder"
        :refund-status="computedRefundStatus"
        @timeup="onTimeup"
      />
      <!-- 客户代表与顾客信息 -->
      <OrderContact type="order" :order="order" :is-base-order="isBaseOrder" />
      <template v-if="isBaseOrder">
        <!-- 配送信息栏 status >= 20 -->
        <OrderDeliveryTile
          v-if="order.status >= 20 && order.status < 70"
          :order="order"
          :is-creator="isCreator"
          :is-status-sent="isStatusSent"
          :refund-status="computedRefundStatus"
          @resetdelivery="resetDelivery"
        />
        <!-- 商品列表区 -->
        <OrderItems
          :order="order"
          :is-creator="isCreator"
          :show-title="order.status >= 20 && order.status <= 60"
          @readjustprice="onReAdjustPrice"
          @openImageViewer="openImageViewer"
        />
        <!-- 订单基础信息区 -->
        <OrderDetails
          :order="order"
          :is-creator="isCreator"
          :refund-status="computedRefundStatus"
          @modifyposnumber="modifyPosNumber"
        />
        <!-- 订单支付信息区 -->
        <OrderPayDetail v-if="hasPayData" :data="order.orderPayLogVos" />
        <!-- 订单发票信息区 -->
        <OrderBillDetail v-if="hasBill" :data="order.billVo" />
        <!-- 订单退款信息区 -->
        <template v-if="refundSections.length">
          <OrderRefundDetail
            v-for="(item, index) in refundSections"
            :key="index"
            :data="item"
          />
        </template>

        <!-- 上传小票控件 -->
        <OrderReceipts
          :order="order"
          :is-creator="isCreator"
          :disabled="!canModifyReceipt"
          v-model="posReceipts"
          @update="updateReceipt"
        />
      </template>
      <template v-else>
        <!-- 订单基础信息区 -->
        <DepositOrderDetails
          :order="order"
          :is-creator="isCreator"
          :refund-status="computedRefundStatus"
          @modifyposnumber="modifyPosNumber"
        />
        <!-- 订单关联商品区 -->
        <DepositOrderItems
          :order="order"
          :is-creator="isCreator"
          :refund-status="computedRefundStatus"
          @openImageViewer="openImageViewer"
        />
        <!-- 订单支付信息区 -->
        <OrderPayDetail v-if="hasPayData" :data="order.orderPayLogVos" />
        <!-- 订单退款信息区 -->
        <template v-if="refundSections.length">
          <OrderRefundDetail
            v-for="(item, index) in refundSections"
            :key="index"
            :data="item"
          />
        </template>
      </template>
      <!-- 底部填充 -->
      <CBottomPad />
      <!-- 底部按钮区 -->
      <OrderPageButtons
        v-if="hasOperations"
        :order="order"
        :is-creator="isCreator"
        :is-base-order="isBaseOrder"
        :is-status-sent="isStatusSent"
        :is-send-off.sync="isSendOff"
        :can-refund="canRefund"
        :submiting="submiting"
        @applyrefund="applyRefund"
        @ship="ship"
        @cancelshipping="cancelShipping"
        @scan="scan"
        @complete="complete"
      />
    </template>

    <!-- 调价弹层 -->
    <AdjustPricePopup
      :show.sync="showModifyPrice"
      :data="modifyData"
      @submit="submitNewPrice"
      :default-remark="changeRemark"
      :default-price="modifyPrice"
    />

    <!-- 物流弹窗 -->
    <LogisticsDialog
      :show.sync="showLogisticsDialog"
      @other-logistics="handleOtherLogistics"
      @continue-order="handleContinueOrder"
    />

    <!-- 下单成功弹窗 -->
    <OrderSuccessDialog :show.sync="showOrderSuccessDialog" :order="order" />

    <!-- 填写物流单号弹窗 -->
    <LogisticsFormDialog
      :show.sync="showLogisticsFormDialog"
      @confirm="handleLogisticsFormConfirm"
    />

    <!-- 图片查看器 -->
    <ImageViewer
      :visible="imageViewerVisible"
      :images="currentImages"
      :initial-index="currentImageIndex"
      :show-delete-btn="false"
      :show-upload-btn="false"
      @close="closeImageViewer"
      @change="onImageViewerChange"
    />

    <!----| POPUP |---->
    <!-- 通用确认框 -->
    <CConfirm ref="refCConfirm" />
    <!-- 填写POS单号对话框 -->
    <PosNumberPrompt ref="refPosNumberPrompt" />
    <!-- 修改POS单号底部弹窗 -->
    <PosNumberModifyDrawer ref="refPosNumberModifyDrawer" />
    <!-- 查看运单信息对话框 -->
    <DeliveryConfirm ref="refDeliveryConfirm" />
    <!-- 申请退款弹窗底部 -->
    <RefundDrawer ref="refRefundDrawer" />
  </div>
</template>

<script>
  import config from '@conf'
  import { sleep } from '@/utils/timer'
  import { wxworkConfig } from '@/utils/wechat'
  import {
    applyRefund,
    cancelExpress,
    expressOrder,
    getOrderDetailInWorkApi,
    reApplyAdjustPriceApi,
    resetDelivery,
    shipByQrCode,
    uploadPos
  } from '@/api'
  import { mapGetters } from 'vuex'
  import { parseHashQueryString } from '@/utils/string'
  import ImageViewer from '@/components/common/ImageViewer.vue'
  import { getPublicOssUrl } from '@/domainMapping/index'

  import orderPage from '@/mixins/order-page'
  import OrderShareTips from '@/components/order/sections/OrderShareTips.vue'
  import OrderContact from '@/components/order/sections/OrderContact.vue'
  import OrderReceipts from '@/components/order/sections/OrderReceipts.vue'
  import OrderPageButtons from '@/components/order/sections/OrderPageButtons.vue'
  import PosNumberPrompt from '@/components/order/popups/PosNumberPrompt.vue'
  import PosNumberModifyDrawer from '@/components/order/popups/PosNumberModifyDrawer.vue'
  import RefundDrawer from '@/components/order/popups/RefundDrawer.vue'
  import DeliveryConfirm from '@/components/order/popups/DeliveryConfirm.vue'
  import AdjustPricePopup from '@/components/common/AdjustPricePopup.vue'
  import LogisticsDialog from '@/components/order/popups/LogisticsDialog.vue'
  import OrderSuccessDialog from '@/components/order/popups/OrderSuccessDialog.vue'
  import LogisticsFormDialog from '@/components/order/popups/LogisticsFormDialog.vue'

  import _mockOrder from '@/mock/data/order-detail'

  export default {
    mixins: [orderPage],
    components: {
      OrderShareTips,
      OrderContact,
      OrderReceipts,
      OrderPageButtons,
      AdjustPricePopup,
      PosNumberPrompt,
      PosNumberModifyDrawer,
      DeliveryConfirm,
      RefundDrawer,
      LogisticsDialog,
      OrderSuccessDialog,
      LogisticsFormDialog,
      ImageViewer
    },
    data() {
      return {
        console: console,
        posReceipts: [],
        showModifyPrice: false,
        modifyData: {},
        modifyPrice: '',
        changeRemark: '',
        isSendOff: true,
        showLogisticsDialog: false,
        showOrderSuccessDialog: false,
        showLogisticsFormDialog: false,
        currentOrderIndex: -1,
        currentImageIndex: 0,
        currentImages: [],
        imageViewerVisible: false
      }
    },
    computed: {
      ...mapGetters([
        'isSalerAccount',
        'isStoreManagerAccount',
        'isAreaManagerAccount'
      ]),
      isCreator() {
        // 去掉创建人限制
        // return (
        //   this.isSalerAccount &&
        //   this.$store.state.user.userId === this.order.caWwid
        // );
        return this.isSalerAccount
      },
      scanedCode() {
        const { status } = this.order
        if (this.isCreator && this.isBaseOrder && status === 40) {
          return this.$route.query.code || ''
        }
        return ''
      },
      canShipOrScanOrComplete() {
        return (
          this.isCreator &&
          !this.computedRefundStatus.isRefundingOrDone &&
          [30, 40, 80].includes(this.order.status)
        )
      },
      shouldShowPosPrompt() {
        return this.canShipOrScanOrComplete && !this.order.posNumber
      },
      isToShip() {
        return (
          this.canShipOrScanOrComplete &&
          this.isBaseOrder &&
          this.order.status === 30
        )
      },
      canModifyReceipt() {
        return this.isCreator && !this.computedRefundStatus.isRefundingOrDone
      },
      canRefund() {
        // console.log(this.brandInfo.refund?.enabled, this.computedRefundStatus.amount);
        return (
          this.brandInfo.refund?.enabled === true &&
          this.isStatusPaid &&
          !this.computedRefundStatus.isRefundingOrDone
        )
      },
      hasOperations() {
        if (this.computedRefundStatus.isRefundingOrDone) return false
        if (this.isAreaManagerAccount || this.isStoreManagerAccount) {
          return this.canRefund
        }
        if (!this.isCreator) return false
        if ([0, 10, 20, 60, 70].includes(this.order.status)) return false
        // 隐含条件 this.order.status === 30, 40, 80
        if (this.canShipOrScanOrComplete) return true
        // 隐含条件 this.order.status === 50, 60
        return this.isStatusSent
      },
      noOperations() {
        if (this.hasOperations) {
          if (this.isCreator && this.isStatusSent) return this.isSendOff
          return false
        }
        return true
      }
    },
    created() {
      console.log('brandInfo', this.brandInfo)
      wxworkConfig({
        jsApiList: [
          'showOptionMenu',
          'scanQRCode',
          'hideOptionMenu',
          'onMenuShareAppMessage',
          'onMenuShareWechat',
          'onMenuShareTimeline'
        ]
      })
    },
    methods: {
      formatAdjustPriceData(produts, adjustPriceVos) {
        if (!produts || !adjustPriceVos) return []
        const adjustVoMapping = {}
        adjustPriceVos.forEach(item => {
          adjustVoMapping[item.orderItemId] = item
        })
        return produts.map(item => {
          const adjustVo = adjustVoMapping[item.id]
          return {
            ...item,
            adjustPriceInnerRemark: adjustVo?.innerRemark || ''
          }
        })
      },
      checkAdjustPriceVos(order) {
        if (order.adjustPriceVos && order.adjustPriceVos.length) {
          order.orderItems = this.formatAdjustPriceData(
            order.orderItems,
            order.adjustPriceVos
          )
        }
      },
      checkOrderIfFirstLoad() {
        if (!this.loaded) {
          this.loaded = true
          if (this.shouldShowPosPrompt) {
            this.showPosNumberPrompt()
          }
        }
      },
      async updateOrderDetail() {
        try {
          if (this.$route.query.orderNo) {
            const res = await getOrderDetailInWorkApi(this.$route.query.orderNo)
            res.orderItems.forEach(item => {
              const manualUploadImages = JSON.parse(item.manualUploadImages)
              if (!Array.isArray(manualUploadImages)) return
              item.manualUploadImages = manualUploadImages
              item.manualUploadImageShows = manualUploadImages.map(filename => {
                return getPublicOssUrl(filename)
              })
            })
            this.checkAdjustPriceVos(res)
            this.order = res
            if (res.sftcNoDriverAccepted === true) {
              this.showLogisticsDialog = true
            }
          } else if (config.isDevelopment) {
            await sleep(300)
            this.checkAdjustPriceVos(_mockOrder)
            this.order = _mockOrder
            // setInterval(() => {
            //   if (this.order.status < 80) {
            //     this.order.status += 10;
            //   } else {
            //     this.order.status = 0;
            //   }
            // }, 2000);
          } else {
            throw new Error('缺少订单编号')
          }
          this.checkOrderIfFirstLoad()
          this.setPosReceipts()
        } catch (error) {
          return this.checkOrderPermission(error)
        }
      },
      setPosReceipts() {
        try {
          this.posReceipts = JSON.parse(this.order.receipt || '[]').map(
            filename => ({ filename })
          )
        } catch (error) {
          this.posReceipts = []
        }
      },
      async updatePosNumber(posNumber, newReceiptNumber) {
        const {
          id: orderId,
          posNumber: old,
          receipt,
          receiptNumber
        } = this.order
        if (posNumber !== old || newReceiptNumber !== receiptNumber) {
          try {
            this.setSubmitting()
            await uploadPos({
              orderId,
              posNumber,
              receipt,
              receiptNumber: newReceiptNumber || receiptNumber
            })
            await this.updateOrderDetail()
            this.unsetSubmitting()
          } catch (error) {
            this.unsetSubmitting(error?.message)
          }
        }
      },
      async updateReceipt(hasLoading) {
        // console.log("updateReceipt", this.posReceipts);
        const {
          id: orderId,
          posNumber,
          receipt: old,
          receiptNumber
        } = this.order
        const receipt = JSON.stringify(
          this.posReceipts
            .filter(({ filename, deleted }) => filename && !deleted)
            .map(item => item.filename)
        )
        if (receipt !== old) {
          try {
            if (!hasLoading) this.loading()
            await uploadPos({
              orderId,
              posNumber,
              receipt,
              receiptNumber
            })
            await this.updateOrderDetail()
            this.loading(false)
          } catch (error) {
            if (error?.message) this.$toast(error.message)
            else this.loading(false)
          }
        }
      },
      showPosNumberPrompt() {
        console.log(Object.keys(this.$refs))
        this.$refs.refPosNumberPrompt
          .show()
          .then(({ posNumber, receiptNumber }) =>
            this.updatePosNumber(posNumber, receiptNumber)
          )
          .catch(() => {})
      },
      modifyPosNumber() {
        this.$refs.refPosNumberModifyDrawer
          .show(this.order.posNumber, this.order.receiptNumber)
          .then(({ posNumber, receiptNumber }) => {
            this.updatePosNumber(posNumber, receiptNumber)
          })
          .catch(() => {})
      },
      async resetDelivery() {
        try {
          if (await this.alert('是否确认重置物流')) {
            this.setSubmitting()
            const { id: orderId } = this.order
            await resetDelivery(orderId)
            await this.updateOrderDetail()
            this.unsetSubmitting()
          }
        } catch (error) {
          console.log('resetDelivery error', error)
          this.unsetSubmitting(error?.message)
        }
      },
      async ship({ isUrgent, isQuote } = {}) {
        const { id, posNumber } = this.order
        if (posNumber) {
          if (isUrgent) {
            if (
              !(await this.alert({
                message: '确定发货并物流加急吗',
                desc: '物流加急服务会产生额外费用，请您确认后选择'
              }))
            )
              return
          }
          try {
            this.setSubmitting()
            if (isQuote) {
              const insuranceAmount =
                this.order.amount ||
                0 * this.brandInfo.extra.shippingInsurancePercentage ||
                1
              await expressOrder(id, !!isUrgent, insuranceAmount)
            } else {
              await expressOrder(id, !!isUrgent)
            }
            await this.updateOrderDetail()
            this.unsetSubmitting()
            this.$refs.refDeliveryConfirm.show(this.order).catch(() => {})
          } catch (error) {
            this.unsetSubmitting(error?.message)
          }
        } else {
          this.showPosNumberPrompt()
        }
      },
      async cancelShipping(noAlert) {
        try {
          if (!noAlert) {
            if ((await this.alert('确认取消发货？')) === false) return
          }
          this.setSubmitting()
          await cancelExpress(this.order.id)
          await this.updateOrderDetail()
          this.unsetSubmitting()
        } catch (error) {
          this.unsetSubmitting(error?.message)
        }
      },
      async shipByFace(code) {
        if (await this.alert('确认客人已收到货物？')) {
          try {
            this.setSubmitting()
            await shipByQrCode(code)
            await this.updateOrderDetail()
            this.unsetSubmitting()
          } catch (error) {
            this.unsetSubmitting(error?.message)
          }
        }
      },
      checkScanResult(resultStr) {
        console.log('order scan resultStr', resultStr)
        if (resultStr?.includes('#/sa/order')) {
          const query = parseHashQueryString(resultStr)
          if (query.orderNo) {
            if (query.orderNo === this.order.orderNo) {
              this.shipByFace(query.code)
            } else {
              this.$toast('请扫描本订单的核销码')
            }
            return
          }
        }
        this.$toast('请扫描客人提供的核销码')
      },
      async scan() {
        if (this.order.posNumber) {
          if (this.scanedCode) {
            this.shipByFace(this.scanedCode)
          } else {
            wx.scanQRCode({
              needResult: 1,
              desc: '面对面发货',
              scanType: ['qrCode', 'barCode'],
              success: res => {
                console.log('order scan res', res)
                if (res.errMsg === 'scanQRCode:ok') {
                  this.checkScanResult(res.resultStr)
                } else {
                  console.log('order scan notOk', res.errMsg)
                }
              },
              fail: function (err) {
                console.error('order scan fail', err)
                if (err.errMsg.indexOf('function_not_exist') > 0) {
                  this.$toast('版本过低请升级')
                }
              },
              error: function (err) {
                console.error('order scan error', err)
                if (err.errMsg.indexOf('function_not_exist') > 0) {
                  this.$toast('版本过低请升级')
                }
              }
            })
          }
        } else {
          this.showPosNumberPrompt()
        }
      },
      async complete() {
        if (!this.order.posNumber) {
          await this.showPosNumberPrompt()
        }
      },
      async applyRefund() {
        try {
          const formData = await this.$refs.refRefundDrawer.show({
            maxAmount: this.order.payAmount - this.order.totalRefund
          })
          this.setSubmitting()
          const { id: orderId } = this.order
          await applyRefund({ orderId, ...formData })
          await this.updateOrderDetail()
          this.unsetSubmitting()
        } catch (error) {
          this.unsetSubmitting(error?.message)
        }
      },
      onReAdjustPrice(payload) {
        console.log('reAdjustPrice', payload)
        this.showModifyPrice = true
        this.modifyData = {
          data: {
            ...payload.data,
            adjustedPrice: undefined
          }
        }
        this.changeRemark = payload.data.innerRemark || ''
        this.modifyPrice = String(
          payload.data.price || payload.data.originalPrice
        )
      },
      async submitNewPrice({ modifyPrice, changeRemark }) {
        if (modifyPrice == '') {
          this.$toast('调整后价格不可为空')
          return false
        }
        if (modifyPrice == this.modifyData.data.originalPrice) {
          this.$toast('调整后价格应不等同于原价')
          return false
        }
        try {
          this.setSubmitting()
          await reApplyAdjustPriceApi({
            orderItemId: this.modifyData.data.id,
            price: modifyPrice,
            innerRemark: changeRemark
          })

          this.showModifyPrice = false
          await this.updateOrderDetail()
          this.unsetSubmitting('重新发起成功')
        } catch (error) {
          this.unsetSubmitting(error?.message)
        }
      },

      // 处理其他物流方式
      handleOtherLogistics() {
        // 显示填写物流单号弹窗
        this.showLogisticsFormDialog = true
      },

      // 处理物流表单确认
      async handleLogisticsFormConfirm(formData) {
        console.log('物流表单数据:', formData)
        const manualLogisticsCompany = formData.logisticsType
        const manualTransNo = formData.logisticsNumber
        await expressOrder(
          this.order.id,
          false,
          undefined,
          manualLogisticsCompany,
          manualTransNo
        )
        await this.updateOrderDetail()
        this.unsetSubmitting()
      },

      // 处理继续下单
      handleContinueOrder() {
        // 显示下单成功弹窗
        // this.showOrderSuccessDialog = true
      },

      // 图片查看器相关方法
      openImageViewer({ orderIndex, imageIndex, images }) {
        this.currentOrderIndex = orderIndex
        this.currentImageIndex = imageIndex
        this.currentImages = images
        // 初始化item的图片索引
        const currentItem = this.order.orderItems[orderIndex]
        if (!currentItem.hasOwnProperty('manualUploadImagesIndex')) {
          this.$set(currentItem, 'manualUploadImagesIndex', imageIndex)
        }
        this.imageViewerVisible = true
      },
      closeImageViewer() {
        this.imageViewerVisible = false
        this.currentImages = []
        this.currentImageIndex = 0
        this.currentOrderIndex = -1
      },
      onImageViewerChange(index) {
        if (this.currentOrderIndex !== -1) {
          const currentItem = this.order.orderItems[this.currentOrderIndex]
          this.$set(currentItem, 'manualUploadImagesIndex', index)
        }
      }
    }
  }
</script>

<style lang="less" scoped>
  .order-page {
    width: 100%;
    height: 100%;
    padding-bottom: 120/20rem;
    overflow-x: hidden;
    overflow-y: auto;

    &.no-operations {
      padding-bottom: 50/20rem;
    }

    &.is-to-ship {
      padding-bottom: 180/20rem;
    }
  }
</style>
